import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UIPreviewContext = createContext({
  modalUiV2: false,
  navUiV2: false,
  refreshSiteWide: () => {},
});

/**
 * App-wide resolver for the in-progress full-site UI rebuild feature flags
 * (Modal UI v2, Nav/Header UI v2). Effective flag = the current user's own
 * personal preview field (super_admin only, set in Settings > General >
 * UI Preview) OR the site-wide default (also super_admin only, same page).
 * New UI work should branch on these, e.g.: `const { modalUiV2 } = useUIPreview();`
 */
export const UIPreviewProvider = ({ user, children }) => {
  const [siteWide, setSiteWide] = useState({ modal_ui_v2_site_wide: false, nav_ui_v2_site_wide: false });

  const refreshSiteWide = useCallback(() => {
    api.get(`${API}/settings/ui-preview`)
      .then(res => setSiteWide(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { refreshSiteWide(); }, [refreshSiteWide]);

  const value = useMemo(() => ({
    modalUiV2: !!(user?.modal_ui_v2_enabled || siteWide.modal_ui_v2_site_wide),
    navUiV2: !!(user?.nav_ui_v2_enabled || siteWide.nav_ui_v2_site_wide),
    refreshSiteWide,
  }), [user?.modal_ui_v2_enabled, user?.nav_ui_v2_enabled, siteWide, refreshSiteWide]);

  return <UIPreviewContext.Provider value={value}>{children}</UIPreviewContext.Provider>;
};

export const useUIPreview = () => useContext(UIPreviewContext);
export default UIPreviewContext;
